import { useEffect, useRef, useState } from 'react';

interface BarcodeScannerProps {
  open: boolean;
  onClose: () => void;
  onDetected: (code: string) => void;
}

declare global {
  interface Window {
    BarcodeDetector?: new (options?: { formats?: string[] }) => {
      detect: (source: HTMLVideoElement | HTMLImageElement) => Promise<{ rawValue: string }[]>;
    };
  }
}

export default function BarcodeScanner({ open, onClose, onDetected }: BarcodeScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [supported, setSupported] = useState<boolean>(typeof window !== 'undefined' && 'BarcodeDetector' in window);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let raf = 0;
    let stopped = false;

    const start = async () => {
      setError(null);
      if (!supported) {
        setError('Tu navegador no soporta el escaneo por cámara. Usa un lector de código de barras o escribe el código manualmente.');
        return;
      }
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment' },
        });
        if (stopped) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }

        const detector = new window.BarcodeDetector!({ formats: ['ean_13', 'ean_8', 'code_128', 'code_39', 'upc_a', 'upc_e', 'qr_code'] });

        const scan = async () => {
          if (stopped || !videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            if (codes.length > 0) {
              const code = codes[0].rawValue;
              stop();
              onDetected(code);
              return;
            }
          } catch {
            // ignorar errores de detección puntuales
          }
          raf = requestAnimationFrame(scan);
        };
        raf = requestAnimationFrame(scan);
      } catch {
        setError('No se pudo acceder a la cámara. Revisa los permisos.');
      }
    };

    const stop = () => {
      stopped = true;
      if (raf) cancelAnimationFrame(raf);
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      }
    };

    start();
    return stop;
  }, [open, supported, onDetected]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden="true" />
      <div className="relative z-10 w-full max-w-sm rounded-lg bg-background-50 border border-background-200 p-5">
        <div className="flex items-center justify-between">
          <h3 className="font-heading text-lg font-semibold text-foreground-950">Escanear código</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-md text-foreground-500 hover:bg-background-100 cursor-pointer"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="mt-4">
          {error ? (
            <div className="rounded-lg bg-background-100 p-6 text-center">
              <i className="ri-camera-off-line text-3xl text-foreground-400" />
              <p className="mt-3 text-sm text-foreground-600">{error}</p>
            </div>
          ) : (
            <div className="relative w-full aspect-square rounded-md overflow-hidden bg-black">
              <video ref={videoRef} className="w-full h-full object-cover" playsInline muted />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-48 h-48 border-2 border-accent-500/70 rounded-lg" />
              </div>
            </div>
          )}
        </div>

        <p className="mt-4 text-xs text-foreground-500 text-center">
          Apunta la cámara al código de barra del producto.
        </p>
      </div>
    </div>
  );
}